import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { X } from 'lucide-react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  editAdPlacementSchema,
  type AdvertisementData,
  type AvailableADDatesType,
  type EditAdPlacementType,
} from '@/schemas/sponsor.schems'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  getAdPlacementTypes,
  getAdvertisementDetail,
  getAvailableADDates,
  updateAdvertisementPlacement,
} from '@/api/sponsorApi'
import { SPONSOR_QUERY_KEYS } from '@/api/queryKeys'
import { showErrorToast, showSuccessToast } from '../ui/toast'
import { DatePicker } from '../ui/date-picker'
import { useEffect, useMemo, useState } from 'react'
import { createAdDateValidator } from '@/utils/dateValidation'
import { queryClient } from '@/main'
import { handleAxiosError } from '@/utils/helper'
import CustomLoader from '../CustomLoader'
import { cn } from '@/lib/utils'
import { getNormalizedAdDates } from '@/constants/sponsor'

interface EditAdPlacementDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  adId: string
  sponsorShipId: string
}

interface AdPlacementType {
  key: string
  placement: string
}

export default function EditAdPlacementDrawer({
  open,
  onOpenChange,
  adId,
  sponsorShipId,
}: Readonly<EditAdPlacementDrawerProps>) {
  const [originalDates, setOriginalDates] = useState<{
    startDate?: Date
    endDate?: Date
  }>({})
  const [originalAdType, setOriginalAdType] = useState<string>('')
  const [isDataLoading, setIsDataLoading] = useState(false)

  const form = useForm<EditAdPlacementType>({
    resolver: zodResolver(editAdPlacementSchema),
    defaultValues: {
      adType: '',
      adStartDate: undefined,
      adEndDate: undefined,
    },
    mode: 'onChange',
  })

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    setError,
    formState: { errors, isDirty, isValid },
  } = form

  const { data: advertisementData, isLoading: isLoadingAdData } = useQuery({
    queryKey: SPONSOR_QUERY_KEYS.advertisementById(adId),
    queryFn: () => getAdvertisementDetail({ id: adId }),
    enabled: !!adId && open,
  })

  const { data: adPlacementTypesData } = useQuery({
    queryKey: SPONSOR_QUERY_KEYS.adPlacementTypes(sponsorShipId),
    queryFn: () => getAdPlacementTypes({ sponsorshipId: sponsorShipId }),
    enabled: !!sponsorShipId && open,
  })
  const adPlacementTypes: AdPlacementType[] = adPlacementTypesData?.data

  useEffect(() => {
    const loadData = async () => {
      if (advertisementData) {
        setIsDataLoading(true)
        try {
          const adData: AdvertisementData = advertisementData?.data

          const originalStartDate = new Date(adData.startDate)
          const originalEndDate = new Date(adData.endDate)

          setOriginalDates({
            startDate: originalStartDate,
            endDate: originalEndDate,
          })
          setOriginalAdType(adData.placement)

          reset({
            adType: adData.placement,
            adStartDate: originalStartDate,
            adEndDate: originalEndDate,
          })
        } catch {
          showErrorToast('Failed to load ad data')
        } finally {
          setIsDataLoading(false)
        }
      }
    }

    loadData()
  }, [advertisementData])

  const watchedValues = useWatch({
    control,
    name: ['adType', 'adStartDate', 'adEndDate'],
  })

  const [adType, adStartDate, adEndDate] = watchedValues

  const isAdTypeSameAsOriginal = () => {
    return adType === originalAdType
  }

  const handleDateChange = (
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => {
    setValue('adStartDate', startDate!, {
      shouldDirty: true,
      shouldValidate: true,
    })
    setValue('adEndDate', endDate!, {
      shouldDirty: true,
      shouldValidate: true,
    })
    clearErrors(['adStartDate', 'adEndDate'])
  }

  const { data: availableAdDate } = useQuery({
    queryKey: [SPONSOR_QUERY_KEYS.availableAdDates, adType],
    queryFn: () =>
      getAvailableADDates({
        placement: adType,
        sponsorshipId: sponsorShipId,
        advertisementId: adId,
      }),
    enabled: !!sponsorShipId && !!adType && open,
  })

  const availableADDatedata: AvailableADDatesType = availableAdDate?.data
  const heroDuration = availableADDatedata?.heroDuration

  const normalizedAvailableDates = useMemo(() => {
    return getNormalizedAdDates(availableADDatedata)
  }, [availableADDatedata])

  const dateValidation = createAdDateValidator(
    normalizedAvailableDates,
    adStartDate,
    adEndDate,
    heroDuration
  )

  const isStartDateInPast = () => {
    if (!originalDates.startDate) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const startDate = new Date(originalDates.startDate)
    startDate.setHours(0, 0, 0, 0)
    return startDate < today
  }

  const handleDateReset = (newAdType?: string) => {
    if (newAdType === originalAdType && isStartDateInPast()) {
      handleDateChange(originalDates.startDate, undefined)
    } else {
      handleDateChange(undefined, undefined)
    }
  }

  const validateDates = () => {
    if (adStartDate) {
      const startError = dateValidation.getError(adStartDate, 'start')
      if (startError) {
        setError('adStartDate', {
          type: 'manual',
          message: startError,
        })
      } else {
        clearErrors('adStartDate')
      }
    } else {
      clearErrors('adStartDate')
    }

    if (adEndDate) {
      const endError = dateValidation.getError(adEndDate, 'end')
      if (endError) {
        setError('adEndDate', {
          type: 'manual',
          message: endError,
        })
      } else {
        clearErrors('adEndDate')
      }
    } else {
      clearErrors('adEndDate')
    }
  }

  const {
    mutate: updateAdvertisementStatusMutation,
    isPending: isUpdatingAdvertisementStatus,
  } = useMutation({
    mutationFn: updateAdvertisementPlacement,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SPONSOR_QUERY_KEYS.advertisementList],
      })
      queryClient.invalidateQueries({
        queryKey: [SPONSOR_QUERY_KEYS.advertisementById(adId)],
      })

      onOpenChange(false)
      showSuccessToast(
        'Ad placement have been successfully updated',
        'Changes are now live on the website.'
      )
    },
    onError: error => {
      handleAxiosError('Failed to update advertisement placement', error)
    },
  })

  const onSubmit = async (data: EditAdPlacementType) => {
    try {
      updateAdvertisementStatusMutation({
        id: adId,
        payload: {
          placement: data.adType,
          startDate: new Date(data.adStartDate.setHours(0, 0, 0, 0)),
          endDate: new Date(data.adEndDate.setHours(23, 59, 59, 999)),
        },
      })
    } catch (error) {
      console.error('Error while editing advertisement placement:', error)
    }
  }

  const onError = () => {
    showErrorToast(
      'Validation Error',
      'Please fill all required fields correctly'
    )
  }

  const handleReset = () => {
    reset({
      adType: originalAdType,
      adStartDate: originalDates.startDate,
      adEndDate: originalDates.endDate,
    })
    clearErrors()
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      direction="right"
      aria-label="Edit ad placement"
    >
      <DrawerContent className="w-full sm:w-[400px] h-full bg-ss-neutral-white border-l border-ss-neutral-50 rounded-none lg:rounded-xl">
        <DrawerHeader className="flex flex-row items-center justify-between px-4 py-3 border-b border-ss-neutral-50">
          <DrawerTitle
            className="text-lg font-semibold text-ss-neutral-950 leading-7"
            id="edit-ad-placement-drawer-title"
          >
            Edit Ad Placement
          </DrawerTitle>
          <DrawerClose asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-ss-action-gray-50"
              aria-label="Close edit ad placement drawer"
            >
              <X className="h-5 w-5 text-ss-neutral-600" aria-hidden="true" />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        {isLoadingAdData || isDataLoading ? (
          <CustomLoader />
        ) : (
          <>
            <form
              onSubmit={handleSubmit(onSubmit, onError)}
              className="h-full flex flex-col justify-between"
            >
              <div className="p-4 flex flex-col gap-4">
                {/* Ad Type Field */}
                <div className="space-y-2">
                  <Label
                    variant={'required'}
                    htmlFor="adType"
                    className="text-sm font-semibold text-ss-neutral-950 leading-4.5 "
                  >
                    Placement
                  </Label>
                  <Select
                    value={adType || ''}
                    onValueChange={val => {
                      setValue('adType', val, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                      handleDateReset(val)
                    }}
                    key={originalAdType}
                  >
                    <SelectTrigger
                      id="adType"
                      size="lg"
                      className={cn(
                        'h-11 border-ss-neutral-100 w-full',
                        errors.adType && 'border-ss-critical-200'
                      )}
                      aria-invalid={!!errors.adType}
                      aria-describedby={
                        errors.adType ? 'adType-error' : undefined
                      }
                    >
                      <SelectValue placeholder="Select a type" />
                    </SelectTrigger>
                    <SelectContent>
                      {adPlacementTypes?.map(type => (
                        <SelectItem key={type.key} value={type.key}>
                          {type.placement}
                        </SelectItem>
                      ))}
                    </SelectContent>
                    {form.getValues('adType') === 'hero' && (
                      <p className="text-sm text-ss-neutral-500 mt-1">
                        (Hero Advertisement visibility duration is{' '}
                        {heroDuration} {heroDuration === 1 ? 'day' : 'days'})
                      </p>
                    )}
                  </Select>
                  {errors.adType && (
                    <div
                      className="flex items-center gap-1 text-sm text-ss-critical-200"
                      role="alert"
                    >
                      <span id="adType-error">{errors.adType.message}</span>
                    </div>
                  )}
                </div>

                {/* Date Fields */}
                <div className="flex flex-wrap gap-4">
                  {/* Start Date */}
                  <div className="flex-1 space-y-2">
                    <Label
                      variant={'required'}
                      htmlFor="adStartDate"
                      className="text-sm font-semibold text-ss-neutral-950 leading-4.5 "
                    >
                      Ad Start Date
                    </Label>
                    <DatePicker
                      id="adStartDate"
                      disabled={
                        !adType ||
                        (isAdTypeSameAsOriginal() && isStartDateInPast())
                      }
                      value={adStartDate}
                      onChange={val => {
                        setValue('adStartDate', val!, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                        validateDates()
                      }}
                      placeholder="Select start date"
                      className={cn(
                        'h-11 border-ss-neutral-100',
                        errors.adStartDate && 'border-ss-critical-200'
                      )}
                      disableDate={date => dateValidation.isDisabled(date)}
                      aria-invalid={!!errors.adStartDate}
                      aria-describedby={
                        errors.adStartDate ? 'adStartDate-error' : undefined
                      }
                    />
                    {errors.adStartDate && (
                      <div
                        className="flex items-center gap-1 text-sm text-ss-critical-200"
                        role="alert"
                      >
                        <span id="adStartDate-error">
                          {errors.adStartDate.message}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* End Date */}
                  <div className="flex-1 space-y-2">
                    <Label
                      variant={'required'}
                      htmlFor="adEndDate"
                      className="text-sm font-semibold text-ss-neutral-950 leading-4.5 "
                    >
                      Ad End Date
                    </Label>
                    <DatePicker
                      id="adEndDate"
                      disabled={!adType}
                      value={adEndDate}
                      onChange={val => {
                        setValue('adEndDate', val!, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                        validateDates()
                      }}
                      placeholder="Select end date"
                      className={cn(
                        'h-11 border-ss-neutral-100',
                        errors.adEndDate && 'border-ss-critical-200'
                      )}
                      disableDate={date =>
                        dateValidation.isDisabled(date, true)
                      }
                      aria-invalid={!!errors.adEndDate}
                      aria-describedby={
                        errors.adEndDate ? 'adEndDate-error' : undefined
                      }
                    />
                    {errors.adEndDate && (
                      <div
                        className="flex items-center gap-1 text-sm text-ss-critical-200"
                        role="alert"
                      >
                        <span id="adEndDate-error">
                          {errors.adEndDate.message}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {!adType && (
                  <p className="text-xs text-ss-neutral-500 mt-1">
                    Please select an ad type first
                  </p>
                )}
              </div>

              <DrawerFooter className="px-6 py-4 border-t border-ss-neutral-50 bg-ss-neutral-white shadow-lg rounded-bl-none rounded-br-none lg:rounded-bl-xl lg:rounded-br-xl">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between w-full gap-3 sm:gap-0">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-9 px-4 border-ss-neutral-200 text-ss-neutral-950 hover:bg-ss-action-gray-50 order-2 sm:order-1"
                    aria-label="Reset form to original values"
                    onClick={handleReset}
                    disabled={!isDirty || isUpdatingAdvertisementStatus}
                  >
                    Reset
                  </Button>
                  <Button
                    type="submit"
                    className="h-9 px-3 bg-ss-blue-900 text-ss-neutral-white hover:bg-ss-blue-800 order-1 sm:order-2"
                    aria-label="Save ad placement changes"
                    disabled={
                      !isDirty || !isValid || isUpdatingAdvertisementStatus
                    }
                  >
                    {isUpdatingAdvertisementStatus
                      ? 'Updating Parameters...'
                      : 'Save & Apply Parameters'}
                  </Button>
                </div>
              </DrawerFooter>
            </form>
          </>
        )}
      </DrawerContent>
    </Drawer>
  )
}
